import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type {
  CreatePoliceReport,
  OfficerDetail,
  PoliceReportAttestationStatus,
  UpdatePoliceReport2,
} from '@/services/api/generated/policeReportsService';

import { PoliceDepartmentPicker } from './PoliceDepartmentPicker';
import { PoliceStatusSelector } from './PoliceStatusSelector';

interface PoliceReportFormProps {
  initialData: Partial<CreatePoliceReport | UpdatePoliceReport2>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading: boolean;
  submitButtonText: string;
  isEditing?: boolean;
}

export const PoliceReportForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  submitButtonText,
  isEditing = false,
}: PoliceReportFormProps) => {
  const [formData, setFormData] = useState({
    attestation_status: 'unclear' as PoliceReportAttestationStatus,
    description: '',
    attestation_description: '',
    report_identifier: '',
    reported_incident_date: '',
    reported_location: '',
    police_department_id: '',
    involved_officers: [] as OfficerDetail[],
    ...initialData,
  });

  const [showOfficers, setShowOfficers] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, ...initialData }));
    // Show officers section if there are existing officers
    setShowOfficers(
      Boolean(
        initialData.involved_officers &&
          initialData.involved_officers.length > 0,
      ),
    );
  }, [initialData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOfficer = () => {
    const newOfficer: OfficerDetail = {
      name: '',
      badge_number: null,
      context: null,
    };
    setFormData((prev) => ({
      ...prev,
      involved_officers: [...(prev.involved_officers || []), newOfficer],
    }));
    setShowOfficers(true);
  };

  const handleUpdateOfficer = (
    index: number,
    field: keyof OfficerDetail,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      involved_officers:
        prev.involved_officers?.map((officer, i) =>
          i === index ? { ...officer, [field]: value || null } : officer,
        ) || [],
    }));
  };

  const handleRemoveOfficer = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      involved_officers:
        prev.involved_officers?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      // Convert empty date string to null
      reported_incident_date: formData.reported_incident_date?.trim()
        ? formData.reported_incident_date.trim()
        : null,
      // Convert empty strings to null for other optional fields
      description: formData.description?.trim() || null,
      attestation_description: formData.attestation_description?.trim() || null,
      report_identifier: formData.report_identifier?.trim() || null,
      reported_location: formData.reported_location?.trim() || null,
      // Filter out empty officers
      involved_officers: formData.involved_officers?.filter(
        (officer) => officer.name.trim() !== '',
      ),
    };

    onSubmit(submitData);
  };

  const isFormValid = () => {
    return (
      formData.attestation_status && formData.police_department_id && !isLoading
    );
  };

  const officerCount = formData.involved_officers?.length || 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Status Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Report Status
        </h3>

        <div className="space-y-4">
          <div>
            <Label
              htmlFor="status"
              className="text-sm font-medium text-gray-700"
            >
              Current Status *
            </Label>
            <div className="mt-2">
              <PoliceStatusSelector
                currentStatus={
                  formData.attestation_status as PoliceReportAttestationStatus
                }
                onStatusChange={(status) =>
                  handleInputChange('attestation_status', status)
                }
                showQuickActions={isEditing}
              />
            </div>
          </div>

          <div>
            <Label
              htmlFor="attestation_description"
              className="text-sm font-medium text-gray-700"
            >
              Status Notes
            </Label>
            <Textarea
              id="attestation_description"
              value={formData.attestation_description || ''}
              onChange={(e) =>
                handleInputChange('attestation_description', e.target.value)
              }
              placeholder="Describe the current status or any recent updates..."
              rows={2}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* Police Department Selection */}
      {!isEditing && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Police Department
          </h3>
          <div>
            <Label
              htmlFor="department"
              className="text-sm font-medium text-gray-700"
            >
              Department *
            </Label>
            <div className="mt-2">
              <PoliceDepartmentPicker
                selectedDepartmentId={formData.police_department_id}
                onDepartmentSelect={(deptId) =>
                  handleInputChange('police_department_id', deptId)
                }
                placeholder="Search for police department..."
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* Report Details - Prioritizing Report ID */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Report Details
        </h3>

        {/* Report Identifier - Make this prominent */}
        <div className="mb-6">
          <Label
            htmlFor="report_identifier"
            className="text-sm font-medium text-gray-700"
          >
            Police Report Number
          </Label>
          <Input
            id="report_identifier"
            type="text"
            value={formData.report_identifier || ''}
            onChange={(e) =>
              handleInputChange('report_identifier', e.target.value)
            }
            placeholder="Report number, case ID, or incident number"
            className="mt-1 text-base font-mono"
          />
          <p className="text-sm text-gray-500 mt-1">
            The most important identifier for tracking this police report
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label
              htmlFor="reported_incident_date"
              className="text-sm font-medium text-gray-700"
            >
              Incident Date
            </Label>
            <Input
              id="reported_incident_date"
              type="date"
              value={formData.reported_incident_date || ''}
              onChange={(e) =>
                handleInputChange('reported_incident_date', e.target.value)
              }
              className="mt-1"
            />
          </div>

          <div>
            <Label
              htmlFor="reported_location"
              className="text-sm font-medium text-gray-700"
            >
              Incident Location
            </Label>
            <Input
              id="reported_location"
              type="text"
              value={formData.reported_location || ''}
              onChange={(e) =>
                handleInputChange('reported_location', e.target.value)
              }
              placeholder="Street address or intersection"
              className="mt-1"
            />
          </div>
        </div>

        <div className="mt-6">
          <Label
            htmlFor="description"
            className="text-sm font-medium text-gray-700"
          >
            Description
          </Label>
          <Textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the police involvement and what happened..."
            rows={3}
            className="mt-1"
          />
        </div>
      </div>

      {/* Officers Section - Collapsible and smaller */}
      <div>
        <Collapsible open={showOfficers} onOpenChange={setShowOfficers}>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <span>Officers {officerCount > 0 && `(${officerCount})`}</span>
                <span className="text-xs">{showOfficers ? '▼' : '▶'}</span>
              </Button>
            </CollapsibleTrigger>

            {showOfficers && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddOfficer}
                className="flex items-center space-x-1"
              >
                <PlusIcon className="h-3 w-3" />
                <span>Add</span>
              </Button>
            )}
          </div>

          <CollapsibleContent className="space-y-3 mt-3">
            {formData.involved_officers?.map((officer, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">
                    Officer {index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveOfficer(index)}
                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="text"
                    value={officer.name}
                    onChange={(e) =>
                      handleUpdateOfficer(index, 'name', e.target.value)
                    }
                    placeholder="Officer name"
                    className="text-sm"
                    required
                  />
                  <Input
                    type="text"
                    value={officer.badge_number || ''}
                    onChange={(e) =>
                      handleUpdateOfficer(index, 'badge_number', e.target.value)
                    }
                    placeholder="Badge #"
                    className="text-sm"
                  />
                </div>
              </div>
            ))}

            {!showOfficers && (
              <div className="text-center py-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddOfficer}
                  className="flex items-center space-x-1"
                >
                  <PlusIcon className="h-3 w-3" />
                  <span>Add Officer Details</span>
                </Button>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {!showOfficers && officerCount === 0 && (
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddOfficer}
              className="flex items-center space-x-1 text-gray-500"
            >
              <PlusIcon className="h-3 w-3" />
              <span>Add Officer Details (Optional)</span>
            </Button>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isFormValid()}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Saving...</span>
            </div>
          ) : (
            submitButtonText
          )}
        </Button>
      </div>
    </form>
  );
};
